<template>
    <div v-if="!isInitialized">
        <div v-if="!travelDocumentsRetrievingFailed">Is loading...</div>
        <div v-else>Something went wrong retrieving travel documents.</div>
    </div>
    <div v-else>
        <div>
            <div class="row">
                <div class="col-8">
                    <strong>{{ title }}</strong> <small>({{ travelDocument.files.length }} file(s), {{ amountOrdered }}
                    {{ orderedItemName }} ordered)</small>
                </div>
                <div class="col">
                    <button v-if="!isMarkingCompleted && canComplete"
                            v-on:click="markCompleted" type="button" class="btn btn-primary pull-right complete-button">
                        Mark Complete
                    </button>
                    <div v-else-if="travelDocument.completed_at" class="d-flex justify-content-end">
                        <button v-on:click="revokeDocument" :disabled="isRevokingDocument"
                                class="btn btn-link btn-xs p-0 text-danger pr-2" ref="revokeDocumentButton" type="button">
                            Revoke
                        </button>
                        <button type="button"
                                class="btn btn-primary pull-right complete-button" disabled>
                            Completed
                        </button>
                    </div>
                    <button v-else type="button" class="btn btn-primary pull-right complete-button" disabled>Mark
                        Complete
                    </button>
                </div>
            </div>
            <div class="row" v-for="categoryInfo in travelDocument.categories_info">
                <div class="col" v-if="categoryInfo.orderedTicketCategoryId !== categoryInfo.receivedTicketCategoryId">
                    <span class="text-gray-600">
                        {{
                            categoryInfo.orderedTicketCategoryName
                        }} is upgraded to {{ categoryInfo.receivedTicketCategoryName }}
                    </span>
                </div>
            </div>
            <table v-if="travelDocument.files.length > 0" class="table m-0">
                <thead>
                <tr>
                    <th>File name</th>
                    <th>Source</th>
                    <th>Downloaded</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tr v-for="file in travelDocument.files">
                    <td><a :href="file.documentUrl">{{ file.originalFileName }}</a></td>
                    <td>{{ file.source }}</td>
                    <td>{{ file.downloadCount }}</td>
                    <td>
                        <button v-if="file.source === 'MANUAL_UPLOAD' && !travelDocument.completed_at"
                                v-on:click="deleteFile(file)" :disabled="isDeletingFile || isMarkingCompleted"
                                class="btn btn-link btn-xs p-0 text-danger" ref="deleteFileButton" type="button">
                            delete
                        </button>
                    </td>
                </tr>
            </table>
            <div v-if="!travelDocument.completed_at">
                <div v-if="isUploading" class="text-center">
                    <i class="mdi mdi-spin mdi-loading text-gray-500"></i>
                </div>
                <vue-dropzone
                        :useCustomSlot="true"
                        id="customDropzone"
                        :options="dropzoneOptions"
                        :include-styling="false"
                        @vdropzone-success-multiple="successfulUpload"
                        @vdropzone-files-added="onFilesAdded">
                    <div class="dropzone-custom-content">
                        <div class="dropzone-container">
                            <span class="dropzone-container-text">Upload {{ title }}</span>
                        </div>
                    </div>
                </vue-dropzone>
            </div>
        </div>
    </div>
</template>

<script>
import modelFormLocker from '../modelFormLocker';
import vueDropzone from 'vue2-dropzone';
import _ from 'lodash';

export default {
    name: "OrderTravelDocuments",
    components: {
        modelFormLocker,
        vueDropzone
    },

    props: {
        title: {
            default: 'Files'
        },
        getEndpoint: {
            required: true
        },
        storeEndpoint: {
            required: true
        },
        amountOrdered: {
            required: true
        },
        orderedItemName: {
            default: 'items'
        },
        travelDocumentsHaveBeenSent: {
            required: true
        }
    },

    data: function () {
        return {
            travelDocument: {
                id: null,
                completed_at: null,
                files: [],
                categories_info: []
            },
            isInitialized: false,
            isUploading: false,
            travelDocumentsRetrievingFailed: false,
            isMarkingCompleted: false,
            isDeletingFile: false,
            isRevokingDocument: false,
            dropzoneOptions: {
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                },
                url: this.storeEndpoint,
                includeStyling: false,
                uploadMultiple: true,
                previewsContainer: false
            }
        }
    },

    computed: {
        canComplete() {
            return !this.travelDocument.completed_at && this.travelDocument.id && this.travelDocument.files.length > 0;
        },

        getUpgrades() {
            if (this.travelDocument?.categories_info === null) {
                return null;
            }
            let upgrades = [];
            this.travelDocument?.categories_info.forEach((categoryInfo) => {
                if (categoryInfo.orderedTicketCategoryId !== categoryInfo.receivedTicketCategoryId) {
                    upgrades[categoryInfo.orderedTicketCategoryName] = categoryInfo.receivedTicketCategoryName;
                }
            })

            return upgrades;
        },
    },

    mounted() {
        this.loadTravelDocument();
    },

    methods: {
        loadTravelDocument() {
            axios.get(this.getEndpoint).then((resp) => {
                if (!_.isEmpty(resp.data)) {
                    this.travelDocument = resp.data;
                }

                this.isInitialized = true;
            }).catch(() => {
                this.travelDocumentsRetrievingFailed = true;
                this.isInitialized = true;
            })
        },

        onFilesAdded(files) {
            this.isUploading = true;
        },

        successfulUpload(file, res) {
            this.isUploading = false;
            this.travelDocument = res;
        },

        markCompleted() {
            if (!confirm('Are you sure?')) {
                return;
            }

            this.isMarkingCompleted = true;
            axios.post(
                    '/admin/api/travel-documents/' + this.travelDocument.id + '/mark-completed'
            ).then((res) => {
                this.travelDocument = res.data;
                this.isMarkingCompleted = false;
            }).catch((res) => {
                console.error(res);
                this.isMarkingCompleted = false;
                alert('Something went wrong during upload. Please try again.');
            });
        },

        deleteFile(file) {
            this.isDeletingFile = true;

            axios.delete(
                    '/admin/api/travel-documents/' + this.travelDocument.id + '/files/' + file.id
            ).then(() => {
                this.travelDocument.files = this.travelDocument.files.filter(function (value) {
                    return value.id !== file.id;
                });
                this.isDeletingFile = false;
            }).catch((res) => {
                console.error(res);
                this.isDeletingFile = false;
                alert('Something went wrong during deletion. Please try again.');
            });
        },

        revokeDocument() {
            this.isRevokingDocument = true;

            if (this.travelDocumentsHaveBeenSent) {
                if (!confirm('Documents have already been sent to the customer. Are you sure you want to revoke the documents?')) {
                    this.isRevokingDocument = false;
                    return;
                }
            }

            axios.post(
                    '/admin/api/travel-documents/' + this.travelDocument.id + '/revoke'
            ).then(() => {
                this.clearDocument();
                this.isRevokingDocument = false;
            }).catch(() => {
                this.isRevokingDocument = false;
                alert('Something went wrong with revoking. Please try again.');
            });
        },

        clearDocument() {
            this.travelDocument = {
                id: null,
                completed_at: null,
                files: [],
                categories_info: []
            };
        }
    }
}
</script>

<style scoped>
.complete-button {
    padding: 0 6px;
    line-height: 22px;
    font-size: 12px;
}

.complete-button:disabled {
    background-color: grey;
    border-color: grey;
    box-shadow: none;
}

.dropzone-container {
    text-align: center;
    display: flex;
    flex-direction: column;
    border: 2px dashed #ccc;
    border-radius: 15px;
    margin-top: 4px;
}

.dropzone-container-text {
    padding: 4px;
    color: #ccc;
    font-weight: bold;
}
</style>
